// Le vocabulaire de la boîte et ses entrées sont partagés avec la régie et les agents.
import { z } from 'zod';
export const CANAUX_CONVERSATION = ['demande', 'email'];
export const ETATS_CONVERSATION = ['ouverte', 'traitee', 'indesirable'];
export const CATEGORIES_CONVERSATION = [
    'prix',
    'disponibilite',
    'rendez_vous',
    'presse',
    'pret',
    'candidature',
    'autre',
];
export const ETATS_MESSAGE = ['recu', 'brouillon', 'envoi', 'envoye', 'echec'];
export const ETATS_PIECE_JOINTE = [
    'en_analyse',
    'saine',
    'infectee',
    'trop_volumineuse',
    'effacee',
];
export const VUES_BOITE = [
    'a_traiter',
    'a_moi',
    'non_assignees',
    'en_attente',
    'traitees',
    'autres',
    'indesirables',
    'toutes',
];
const id = z.string().regex(/^[a-f\d]{24}$/i, 'Identifiant invalide.');
export const versionConversationSchema = z.strictObject({ version: z.int().min(0) });
export const modificationConversationSchema = versionConversationSchema.extend({
    categorie: z.enum(CATEGORIES_CONVERSATION).nullable().optional(),
    etiquettes: z.array(z.string().trim().min(1).max(40)).max(30).optional(),
    assigneeA: id.nullable().optional(),
    oeuvreIds: z.array(id).max(20).optional(),
    contactId: id.nullable().optional(),
    nonLue: z.boolean().optional(),
});
export const etatConversationSchema = versionConversationSchema.extend({
    etat: z.enum(['traitee', 'ouverte', 'indesirable', 'principale']),
});
export const attenteConversationSchema = versionConversationSchema.extend({
    jusquau: z.iso.datetime({ offset: true }).nullable(),
});
export const noteBoiteSchema = versionConversationSchema.extend({
    texte: z.string().trim().min(1).max(50_000),
});
export const brouillonBoiteSchema = versionConversationSchema.extend({
    texte: z.string().max(50_000),
});
export const contactConversationSchema = versionConversationSchema.extend({
    contactId: id.optional(),
});
export const expediteurIgnoreSchema = versionConversationSchema.extend({ conversationId: id });
export const reglagesBoiteSchema = z.strictObject({
    version: z.int().min(0),
    adresseReponse: z.enum(['reception', 'galerie']),
    signature: z.string().max(2000),
    etiquettes: z
        .array(z.string().trim().min(1).max(40))
        .max(30)
        .refine((a) => new Set(a).size === a.length, 'Étiquettes répétées.'),
    objectifsHeures: z.record(z.enum(CATEGORIES_CONVERSATION), z.int().min(1).max(720)),
    suiviOuverture: z.boolean(),
    expediteursIgnores: z.array(z.string().trim().toLowerCase().max(200).pipe(z.email())).max(200),
});
export const preferenceBoiteSchema = z.strictObject({
    resumeQuotidien: z.boolean(),
    alertes: z.boolean(),
});
export const OBJECTIFS_BOITE_PAR_DEFAUT = Object.fromEntries(CATEGORIES_CONVERSATION.map((c) => [c, 24]));
export function normaliserSujet(sujet) {
    return sujet.replace(/^(?:\s*(?:re|fw|fwd|tr|aw|wg|sv|rif|réf\.?)\s*:\s*)+/i, '').trim();
}
export function normaliserRecherche(texte) {
    return texte
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/œ/g, 'oe')
        .replace(/æ/g, 'ae')
        .replace(/\s+/g, ' ')
        .trim();
}
export function messageAutomatique(entetes, de) {
    const h = Object.fromEntries(Object.entries(entetes).map(([k, v]) => [k.toLowerCase(), v.trim().toLowerCase()]));
    return Boolean((h['auto-submitted'] && h['auto-submitted'] !== 'no') ||
        /^(bulk|list|junk)(?:\s|$)/.test(h['precedence'] ?? '') ||
        'list-id' in h ||
        'list-unsubscribe' in h ||
        /(?:mailer-daemon|no[-_.]?reply)/i.test(de.split('@')[0] ?? ''));
}
export function estEnHeuresCalmes(date) {
    const heure = Number(new Intl.DateTimeFormat('fr', {
        timeZone: 'Europe/Paris',
        hour: '2-digit',
        hourCycle: 'h23',
    })
        .formatToParts(date)
        .find((p) => p.type === 'hour')?.value);
    return heure >= 20 || heure < 8;
}
export function calculerDelais(conversations, objectifs, maintenant, periodeJours) {
    const debut = maintenant.getTime() - periodeJours * 86_400_000;
    const retenues = conversations.filter((c) => !c.automatique &&
        c.etat !== 'indesirable' &&
        c.premierEntrantLe &&
        c.premierEntrantLe.getTime() >= debut &&
        c.premierEntrantLe <= maintenant);
    const heures = (c, fin) => Math.max(0, (fin.getTime() - c.premierEntrantLe.getTime()) / 3_600_000);
    const objectif = (c) => objectifs[c.categorie ?? 'autre'];
    const statistiques = (groupe) => {
        const repondues = groupe.filter((c) => c.premiereReponseLe && c.premiereReponseLe <= maintenant);
        const delais = repondues.map((c) => heures(c, c.premiereReponseLe)).sort((a, b) => a - b);
        const n = delais.length;
        const dansObjectif = repondues.filter((c) => heures(c, c.premiereReponseLe) <= objectif(c)).length;
        let medianeHeures = null;
        if (n)
            medianeHeures =
                n % 2 ? delais[Math.floor(n / 2)] : (delais[n / 2 - 1] + delais[n / 2]) / 2;
        return {
            conversationsRecues: groupe.length,
            repondues: n,
            medianeHeures,
            p90Heures: n ? delais[Math.ceil(n * 0.9) - 1] : null,
            dansObjectif,
            partDansObjectif: n ? dansObjectif / n : null,
        };
    };
    return {
        jours: periodeJours,
        ...statistiques(retenues),
        parCanal: Object.fromEntries(CANAUX_CONVERSATION.map((canal) => [
            canal,
            statistiques(retenues.filter((c) => c.canal === canal)),
        ])),
        parCategorie: Object.fromEntries(CATEGORIES_CONVERSATION.map((categorie) => [
            categorie,
            statistiques(retenues.filter((c) => (c.categorie ?? 'autre') === categorie)),
        ])),
        ouvertesSansReponse: retenues
            .filter((c) => c.etat === 'ouverte' && !c.premiereReponseLe)
            .sort((a, b) => a.premierEntrantLe.getTime() - b.premierEntrantLe.getTime())
            .map((c) => ({
            id: c.id ?? c._id?.toString(),
            sujet: c.sujet,
            correspondant: c.correspondant,
            canal: c.canal,
            categorie: c.categorie,
            premierEntrantLe: c.premierEntrantLe,
            attenteHeures: heures(c, maintenant),
            retardHeures: Math.max(0, heures(c, maintenant) - objectif(c)),
        })),
    };
}
//# sourceMappingURL=boite-reception.js.map